import { Tour as ATour } from '@ark-ui/solid/tour';
import { IconChevronLeft } from '@bbr/icons/chevron-left-outline';
import { IconChevronRight } from '@bbr/icons/chevron-right-outline';
import { IconX } from '@bbr/icons/x-outline';
import { For, Show } from 'solid-js';
import { Portal } from 'solid-js/web';
import { Button } from '../forms/button.tsx';
import { Heading } from '../text/heading.tsx';
import type { ComponentProps } from 'solid-js';

export { useTour } from '@ark-ui/solid/tour';
export type { TourStepDetails } from '@ark-ui/solid/tour';

export function Tour(props: ComponentProps<typeof ATour.Root>) {
	return (
		<Tour.Root tour={props.tour}>
			<Portal>
				<Tour.Backdrop />
				<Tour.Spotlight />
				<Tour.Positioner>
					<Tour.Content>
						<Tour.Arrow>
							<Tour.ArrowTip />
						</Tour.Arrow>
						<div class="flex flex-row items-center justify-between gap-4 ps-2 pe-0 pt-2 sm:ps-4 sm:pt-4">
							<hgroup class="flex flex-col-reverse">
								<Tour.Title />
								<Tour.ProgressText />
							</hgroup>
							<div class="ms-auto shrink pe-2">
								<Tour.CloseTrigger />
							</div>
						</div>
						<div class="px-3 sm:px-6">
							<Tour.Description />
						</div>
						<Tour.Control class="flex flex-row-reverse flex-wrap items-stretch justify-between gap-4 px-2 pb-2 sm:items-center sm:px-4 sm:pb-4">
							<Tour.Actions>
								{(actions) => <For each={actions()}>{(action) => <Tour.ActionTrigger action={action} />}</For>}
							</Tour.Actions>
						</Tour.Control>
					</Tour.Content>
				</Tour.Positioner>
			</Portal>
		</Tour.Root>
	);
}

Tour.Root = (props: ComponentProps<typeof ATour.Root>) => <ATour.Root {...props} />;

Tour.Backdrop = (props: ComponentProps<typeof ATour.Backdrop>) => (
	<ATour.Backdrop {...props} class="z-50 bg-yellow-950/40" />
);
Tour.Spotlight = (props: ComponentProps<typeof ATour.Spotlight>) => <ATour.Spotlight {...props} class="rounded-xl" />;
Tour.Positioner = (props: ComponentProps<typeof ATour.Positioner>) => (
	<ATour.Positioner
		{...props}
		class="fixed z-50! transition-all duration-200 data-[type=dialog]:inset-0 data-[type=dialog]:m-auto data-[type=dialog]:grid data-[type=dialog]:w-full data-[type=dialog]:place-items-center data-[type=floating]:data-[placement*=bottom]:bottom-8 data-[type=floating]:data-[placement*=center]:inset-0"
	/>
);
Tour.Content = (props: ComponentProps<typeof ATour.Content>) => (
	<ATour.Content
		{...props}
		class="z-50 flex max-h-(--available-height) max-w-(--available-width) flex-col gap-4 rounded-lg border border-zinc-200 bg-white text-lg text-zinc-950 shadow-xl data-[state=closed]:animate-out data-[state=closed]:zoom-out-50 data-[state=closed]:fade-out data-[state=open]:animate-in data-[state=open]:duration-200 data-[state=open]:zoom-in-50 data-[state=open]:fade-in sm:max-w-md sm:min-w-sm dark:border-zinc-800 dark:bg-zinc-950 dark:text-zinc-50"
	/>
);
Tour.Arrow = (props: ComponentProps<typeof ATour.Arrow>) => <ATour.Arrow {...props} />;
Tour.ArrowTip = (props: ComponentProps<typeof ATour.ArrowTip>) => <ATour.ArrowTip {...props} />;
Tour.Title = (props: ComponentProps<typeof ATour.Title>) => (
	<ATour.Title asChild={(p) => <Heading level="h1" size="h5" {...p()} />} {...props} />
);
Tour.Description = (props: ComponentProps<typeof ATour.Description>) => <ATour.Description {...props} />;
Tour.Control = (props: ComponentProps<typeof ATour.Control>) => <ATour.Control {...props} />;
Tour.ProgressText = (props: ComponentProps<typeof ATour.ProgressText>) => (
	<ATour.ProgressText {...props} class="text-sm text-zinc-600 dark:text-zinc-400" />
);
Tour.CloseTrigger = (props: ComponentProps<typeof ATour.CloseTrigger>) => (
	<div class="**:data-icon:size-6 **:data-icon:transition-transform **:data-icon:duration-300 **:data-icon:group-hover/button:rotate-90">
		<ATour.CloseTrigger
			asChild={(p) => (
				// @ts-expect-error too complex
				<Button
					{...p()}
					appearance="subtle"
					rounding="circle"
					tabindex={1}
					icon={IconX}
					iconPosition="only"
					iconLabel="Close"
				/>
			)}
			{...props}
		/>
	</div>
);
Tour.Actions = (props: ComponentProps<typeof ATour.Actions>) => <ATour.Actions {...props} />;
Tour.ActionTrigger = (props: ComponentProps<typeof ATour.ActionTrigger>) => (
	<ATour.ActionTrigger
		{...props}
		asChild={(p) => (
			// @ts-expect-error too complex
			<Button
				{...p()}

				tabIndex={props.action.action === 'next' || props.action.action === 'dismiss' ? 0 : 1}
				appearance={
					props.action.action === 'next' || props.action.action === 'dismiss'
						? 'primary'
						: props.action.action === 'skip'
							? 'outline'
							: 'subtle'
				}
			/>
		)}
	>
		<Show when={props.action.attrs?.prevIcon}>
			<IconChevronLeft />
		</Show>
		{props.action.label}
		<Show when={props.action.attrs?.nextIcon}>
			<IconChevronRight />
		</Show>
	</ATour.ActionTrigger>
);
